import type React from 'react';

import { FocusRing } from '@change/ds-core-ui/util';
import { ChevronLeftIcon } from '@change/ds-icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@change/ds-icons/ChevronRightIcon';

import { Translate } from '@change-corgi/core-react/i18n';

type ButtonProps = { onClick: () => void; disabled: boolean };
type Props = {
	previous: ButtonProps;
	next: ButtonProps;
};

const BUTTON_CLASS =
	'border-stroke-weak bg-background-base text-icon-strong flex h-10 w-10 items-center justify-center rounded-full border disabled:cursor-default disabled:opacity-40';

/**
 * The previous/next pair of `ScrollContainerV2` in its `desktopControls` mode: laid out inline in the
 * scroller's header instead of floating over the list's edges, and only rendered from `md` up, where a
 * pointer user has no swipe. Both buttons stay in the DOM when disabled so the header does not shift.
 */
export const HeaderNavigationButtonsV2: React.FC<Props> = ({ previous, next }) => (
	<div className="hidden shrink-0 items-center gap-3 md:flex">
		<FocusRing focusRingClass="rounded-full">
			<button
				type="button"
				onClick={previous.onClick}
				aria-disabled={previous.disabled}
				disabled={previous.disabled}
				data-testid="previous-button"
				className={BUTTON_CLASS}
			>
				<ChevronLeftIcon size={20} aria-hidden />
				<span className="sr-only">
					<Translate value="corgi.components.scroll_container.navigation_btn.previous" />
				</span>
			</button>
		</FocusRing>
		<FocusRing focusRingClass="rounded-full">
			<button
				type="button"
				onClick={next.onClick}
				aria-disabled={next.disabled}
				disabled={next.disabled}
				data-testid="next-button"
				className={BUTTON_CLASS}
			>
				<ChevronRightIcon size={20} aria-hidden />
				<span className="sr-only">
					<Translate value="corgi.components.scroll_container.navigation_btn.next" />
				</span>
			</button>
		</FocusRing>
	</div>
);
